// «Ver respuestas» on a Satori acknowledgement form (ios/Sources/Views/AcknowledgementSheet.swift,
// PARITY P346). The form's buttons are ordinary interactive attachments, so answering already worked
// — this is the half that was missing: the answers. The tally first ("how many still owe me an
// answer"), then — only when the SERVER says this reader may manage the form (`can_manage`) — who
// answered what. Opened from the message menu; one sheet at a time (ExtrasHost mounts it).
import { useEffect, useState, useSyncExternalStore } from 'react';
import { data } from '../../../bridge/client';
import { Acknowledgements, type AcknowledgementView } from '../../../core/acknowledgements';
import { t } from '../../../i18n/i18n';
import { Dialog } from '../../kit/Kit';
import './extras.css';

let openPostId: string | null = null;
const listeners = new Set<() => void>();

export function openAcknowledgements(postId: string): void {
  openPostId = postId;
  for (const l of listeners) l();
}

function close(): void {
  openPostId = null;
  for (const l of listeners) l();
}

function useOpenPostId(): string | null {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => void listeners.delete(l);
    },
    () => openPostId,
  );
}

export function AcknowledgementHost() {
  const postId = useOpenPostId();
  return postId ? <AcknowledgementSheet key={postId} postId={postId} onClose={close} /> : null;
}

function AcknowledgementSheet({ postId, onClose }: { postId: string; onClose(): void }) {
  const [view, setView] = useState<AcknowledgementView | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    void data
      .call('acknowledgementView', postId)
      .then((r) => {
        if (!live) return;
        if ('view' in r) setView(r.view);
        else setFailed(true);
      })
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [postId]);
  return (
    <Dialog title={view?.form.title || t('ack.title')} onClose={onClose} width={440}>
      <div className="ackform-sheet" data-ack-post={postId}>
        {view ? (
          <>
            {Acknowledgements.tally(view).map((line) => (
              <div key={line.option.id} className="ackform-option" data-option={line.option.id}>
                <div className="ackform-option-head">
                  <span className="ackform-label">{line.option.label}</span>
                  {line.isMine ? <span className="ackform-mine">{t('ack.yourAnswer')}</span> : null}
                  <span className="ackform-count">{line.count}</span>
                </div>
                <div className="ackform-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(line.fraction * 100)} aria-label={line.option.label}>
                  <i style={{ width: `${Math.round(line.fraction * 100)}%` }} />
                </div>
              </div>
            ))}
            {Acknowledgements.showsResponses(view) ? (
              <>
                <div className="ackform-who">{t('ack.whoAnswered')}</div>
                <ul className="ackform-responses">
                  {view.responses.map((r) => (
                    <li key={r.userId}>
                      <span>@{r.username}</span>
                      <span className="ackform-response-option">{r.optionLabel}</span>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </>
        ) : failed ? (
          <p className="ackform-unavailable">{t('ack.unavailable')}</p>
        ) : (
          <div className="ackform-loading" aria-busy="true">
            {t('channel.loading.accessibility')}…
          </div>
        )}
      </div>
    </Dialog>
  );
}
